import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  TextField,
  Button,
  Grid,
  MenuItem,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Typography,
} from '@mui/material';
import { Autocomplete, Popper } from '@mui/material';
import {
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

interface CarSearchFormProps {
  onSearch?: (params: CarSearchParams) => void;
}

interface CarSearchParams {
  pickupLocation: string;
  dropoffLocation: string;
  sameDropoff: boolean;
  pickupDate: string;
  pickupTime: string;
  dropoffDate: string;
  dropoffTime: string;
}

// ============================================================================
// LOCATION DATA & AUTOCOMPLETE
// ============================================================================

interface LocationOption {
  name: string;
  type: 'airport' | 'city' | 'downtown';
  code?: string;
  region: string;
  country: string;
  label: string;
}

const LOCATIONS: LocationOption[] = [
  // US Airports & Cities
  { name: 'Los Angeles International Airport', type: 'airport', code: 'LAX', region: 'California', country: 'US', label: 'LAX - Los Angeles International Airport' },
  { name: 'Los Angeles Downtown', type: 'downtown', region: 'California', country: 'US', label: 'Los Angeles Downtown, CA' },
  { name: 'San Francisco International Airport', type: 'airport', code: 'SFO', region: 'California', country: 'US', label: 'SFO - San Francisco International Airport' },
  { name: 'San Francisco Downtown', type: 'downtown', region: 'California', country: 'US', label: 'San Francisco Downtown, CA' },
  { name: 'JFK International Airport', type: 'airport', code: 'JFK', region: 'New York', country: 'US', label: 'JFK - New York Kennedy Airport' },
  { name: 'LaGuardia Airport', type: 'airport', code: 'LGA', region: 'New York', country: 'US', label: 'LGA - New York LaGuardia Airport' },
  { name: 'Newark Liberty Airport', type: 'airport', code: 'EWR', region: 'New Jersey', country: 'US', label: 'EWR - Newark Liberty Airport' },
  { name: 'Manhattan', type: 'downtown', region: 'New York', country: 'US', label: 'Manhattan, New York, NY' },
  { name: "O'Hare International Airport", type: 'airport', code: 'ORD', region: 'Illinois', country: 'US', label: "ORD - Chicago O'Hare Airport" },
  { name: 'Midway Airport', type: 'airport', code: 'MDW', region: 'Illinois', country: 'US', label: 'MDW - Chicago Midway Airport' },
  { name: 'Chicago Downtown', type: 'downtown', region: 'Illinois', country: 'US', label: 'Chicago Downtown, IL' },
  { name: 'Miami International Airport', type: 'airport', code: 'MIA', region: 'Florida', country: 'US', label: 'MIA - Miami International Airport' },
  { name: 'Fort Lauderdale Airport', type: 'airport', code: 'FLL', region: 'Florida', country: 'US', label: 'FLL - Fort Lauderdale Airport' },
  { name: 'Miami Beach', type: 'downtown', region: 'Florida', country: 'US', label: 'Miami Beach, FL' },
  { name: 'Orlando International Airport', type: 'airport', code: 'MCO', region: 'Florida', country: 'US', label: 'MCO - Orlando International Airport' },
  { name: 'Las Vegas Harry Reid Airport', type: 'airport', code: 'LAS', region: 'Nevada', country: 'US', label: 'LAS - Las Vegas Airport' },
  { name: 'Las Vegas Strip', type: 'downtown', region: 'Nevada', country: 'US', label: 'Las Vegas Strip, NV' },
  { name: 'Seattle-Tacoma Airport', type: 'airport', code: 'SEA', region: 'Washington', country: 'US', label: 'SEA - Seattle-Tacoma Airport' },
  { name: 'Denver International Airport', type: 'airport', code: 'DEN', region: 'Colorado', country: 'US', label: 'DEN - Denver International Airport' },
  { name: 'Boston Logan Airport', type: 'airport', code: 'BOS', region: 'Massachusetts', country: 'US', label: 'BOS - Boston Logan Airport' },
  { name: 'Phoenix Sky Harbor Airport', type: 'airport', code: 'PHX', region: 'Arizona', country: 'US', label: 'PHX - Phoenix Sky Harbor Airport' },
  { name: 'San Diego International Airport', type: 'airport', code: 'SAN', region: 'California', country: 'US', label: 'SAN - San Diego Airport' },
  { name: 'Honolulu Airport', type: 'airport', code: 'HNL', region: 'Hawaii', country: 'US', label: 'HNL - Honolulu Airport' },
  { name: 'Waikiki', type: 'downtown', region: 'Hawaii', country: 'US', label: 'Waikiki, Honolulu, HI' },
  // International
  { name: 'London Heathrow Airport', type: 'airport', code: 'LHR', region: 'England', country: 'UK', label: 'LHR - London Heathrow Airport' },
  { name: 'London Gatwick Airport', type: 'airport', code: 'LGW', region: 'England', country: 'UK', label: 'LGW - London Gatwick Airport' },
  { name: 'Central London', type: 'downtown', region: 'England', country: 'UK', label: 'Central London, UK' },
  { name: 'Paris Charles de Gaulle', type: 'airport', code: 'CDG', region: 'Île-de-France', country: 'France', label: 'CDG - Paris Charles de Gaulle' },
  { name: 'Paris Downtown', type: 'downtown', region: 'Île-de-France', country: 'France', label: 'Paris Downtown, France' },
  { name: 'Cancún Airport', type: 'airport', code: 'CUN', region: 'Quintana Roo', country: 'Mexico', label: 'CUN - Cancún Airport' },
  { name: 'Cancún Hotel Zone', type: 'downtown', region: 'Quintana Roo', country: 'Mexico', label: 'Cancún Hotel Zone, Mexico' },
  { name: 'Toronto Pearson Airport', type: 'airport', code: 'YYZ', region: 'Ontario', country: 'Canada', label: 'YYZ - Toronto Pearson Airport' },
  { name: 'Vancouver Airport', type: 'airport', code: 'YVR', region: 'British Columbia', country: 'Canada', label: 'YVR - Vancouver Airport' },
  { name: 'Dubai Airport', type: 'airport', code: 'DXB', region: 'Dubai', country: 'UAE', label: 'DXB - Dubai International Airport' },
];

const filterLocations = (query: string): LocationOption[] => {
  if (!query || query.length < 2) return [];
  const normalizedQuery = query.toLowerCase().trim();
  
  return LOCATIONS.filter(loc => {
    const searchFields = [
      loc.name.toLowerCase(),
      loc.region.toLowerCase(),
      loc.country.toLowerCase(),
      loc.label.toLowerCase(),
      loc.code?.toLowerCase() || '',
    ];
    return searchFields.some(field => field.includes(normalizedQuery));
  }).slice(0, 8);
};

const AutocompletePopper = (props: any) => (
  <Popper {...props} placement="bottom-start" style={{ zIndex: 1500 }} />
);

const CarSearchForm: React.FC<CarSearchFormProps> = ({ onSearch }) => {
  const navigate = useNavigate();
  const today = new Date().toISOString().split('T')[0];
  const nextWeek = new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0];

  const [formData, setFormData] = useState<CarSearchParams>({
    pickupLocation: '',
    dropoffLocation: '',
    sameDropoff: true,
    pickupDate: today,
    pickupTime: '10:00',
    dropoffDate: nextWeek,
    dropoffTime: '10:00',
  });

  const [dateError, setDateError] = useState('');

  const handleChange = (field: keyof CarSearchParams) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setDateError('');
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    const newData = { ...formData, [field]: value };

    if (field === 'pickupDate' && typeof value === 'string' && value > newData.dropoffDate) {
      const nextWeek = new Date(value);
      nextWeek.setDate(nextWeek.getDate() + 7);
      newData.dropoffDate = nextWeek.toISOString().split('T')[0];
    }

    if (field === 'dropoffDate' && typeof value === 'string' && value < newData.pickupDate) {
      setDateError('Drop-off date must be on or after pick-up date');
      return;
    }

    setFormData(newData);
  };

  const handleSearch = () => {
    if (!formData.pickupLocation.trim()) {
      setDateError('Please enter a pick-up location');
      return;
    }
    if (formData.dropoffDate < formData.pickupDate) {
      setDateError('Drop-off date must be on or after pick-up date');
      return;
    }
    setDateError('');
    if (onSearch) {
      onSearch(formData);
    } else {
      const params = new URLSearchParams({
        pickupLocation: formData.pickupLocation,
        dropoffLocation: formData.sameDropoff ? formData.pickupLocation : formData.dropoffLocation,
        pickupDate: formData.pickupDate,
        pickupTime: formData.pickupTime,
        dropoffDate: formData.dropoffDate,
        dropoffTime: formData.dropoffTime,
      });
      navigate(`/search/cars?${params.toString()}`);
    }
  };

  const timeOptions = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const hour = h.toString().padStart(2, '0');
      const minute = m.toString().padStart(2, '0');
      const time = `${hour}:${minute}`;
      const displayHour = h % 12 || 12;
      const ampm = h < 12 ? 'AM' : 'PM';
      const display = `${displayHour}:${minute} ${ampm}`;
      timeOptions.push({ value: time, label: display });
    }
  }

  return (
    <Box>
      {dateError && (
        <Alert severity="error" onClose={() => setDateError('')} sx={{ mb: 1.5, borderRadius: 2 }}>
          {dateError}
        </Alert>
      )}
      {/* Same dropoff checkbox */}
      <FormControlLabel
        control={
          <Checkbox
            checked={formData.sameDropoff}
            onChange={handleChange('sameDropoff')}
            color="primary"
          />
        }
        label="Return to same location"
        sx={{ mb: 2, '& .MuiFormControlLabel-label': { fontSize: '0.9rem' } }}
      />

      <Grid container spacing={2} alignItems="flex-end">
        {/* Pickup Location */}
        <Grid item xs={12} md={formData.sameDropoff ? 3 : 2.5}>
          <Autocomplete
            freeSolo
            options={filterLocations(formData.pickupLocation)}
            getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
            inputValue={formData.pickupLocation}
            onInputChange={(_, newValue) => setFormData({ ...formData, pickupLocation: newValue })}
            onChange={(_, newValue) => {
              if (newValue && typeof newValue !== 'string') {
                setFormData({ ...formData, pickupLocation: newValue.label });
              }
            }}
            PopperComponent={AutocompletePopper}
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ py: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                  <Box sx={{ 
                    bgcolor: option.type === 'airport' ? '#E3F2FD' : '#FFF3E0',
                    color: option.type === 'airport' ? '#1976D2' : '#E65100',
                    px: 0.75, 
                    py: 0.25, 
                    borderRadius: 1,
                    fontSize: '0.6rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    minWidth: 55,
                    textAlign: 'center',
                  }}>
                    {option.code || option.type}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {option.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.region}, {option.country}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label="Pick-up location"
                placeholder="City or airport"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <LocationIcon color="primary" />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            )}
          />
        </Grid>

        {/* Dropoff Location (if different) */}
        {!formData.sameDropoff && (
          <Grid item xs={12} md={2.5}>
            <Autocomplete
              freeSolo
              options={filterLocations(formData.dropoffLocation)}
              getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
              inputValue={formData.dropoffLocation}
              onInputChange={(_, newValue) => setFormData({ ...formData, dropoffLocation: newValue })}
              onChange={(_, newValue) => {
                if (newValue && typeof newValue !== 'string') {
                  setFormData({ ...formData, dropoffLocation: newValue.label });
                }
              }}
              PopperComponent={AutocompletePopper}
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ py: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                    <Box sx={{ 
                      bgcolor: option.type === 'airport' ? '#E3F2FD' : '#FFF3E0',
                      color: option.type === 'airport' ? '#1976D2' : '#E65100',
                      px: 0.75, 
                      py: 0.25, 
                      borderRadius: 1,
                      fontSize: '0.6rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      minWidth: 55,
                      textAlign: 'center',
                    }}>
                      {option.code || option.type}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {option.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.region}, {option.country}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label="Drop-off location"
                  placeholder="City or airport"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <LocationIcon color="secondary" />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              )}
            />
          </Grid>
        )}

        {/* Pickup Date */}
        <Grid item xs={6} md={formData.sameDropoff ? 2 : 1.5}>
          <TextField
            fullWidth
            label="Pick-up date"
            type="date"
            value={formData.pickupDate}
            onChange={handleChange('pickupDate')}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: today }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarIcon color="primary" sx={{ fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
        </Grid>

        {/* Pickup Time */}
        <Grid item xs={6} md={formData.sameDropoff ? 1.5 : 1.25}>
          <TextField
            fullWidth
            select
            label="Time"
            value={formData.pickupTime}
            onChange={handleChange('pickupTime')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <TimeIcon color="primary" sx={{ fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          >
            {timeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Dropoff Date */}
        <Grid item xs={6} md={formData.sameDropoff ? 2 : 1.5}>
          <TextField
            fullWidth
            label="Drop-off date"
            type="date"
            value={formData.dropoffDate}
            onChange={handleChange('dropoffDate')}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: formData.pickupDate || today }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarIcon color="primary" sx={{ fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
        </Grid>

        {/* Dropoff Time */}
        <Grid item xs={6} md={formData.sameDropoff ? 1.5 : 1.25}>
          <TextField
            fullWidth
            select
            label="Time"
            value={formData.dropoffTime}
            onChange={handleChange('dropoffTime')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <TimeIcon color="primary" sx={{ fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          >
            {timeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Search Button */}
        <Grid item xs={12} md={2}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleSearch}
            disabled={!formData.pickupLocation}
            startIcon={<SearchIcon />}
            sx={{
              py: 1.8,
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
              bgcolor: 'primary.main',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
            }}
          >
            Search Cars
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CarSearchForm;
